import type { WorkerModuleScorecard } from '@insolitum/types';
import { formatWorkerMetric, metricSampleNote, workerModuleState } from '@insolitum/utils';
import { AlertTriangle, ArrowUpRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { getModuleDef } from '../../../modules/registry';

import { formatRelativeDate, TONE_TEXT } from './tone';

/**
 * Wyniki jednego pracownika w jednym organie — kafel kartoteki.
 * ============================================================
 *
 * Malowany BARWĄ TEGO ORGANU, wprost z rejestru (`color`, `icon`). To jest
 * sposób, w jaki kartoteka cytuje język wizualny każdego modułu, nie importując
 * ani jednej jego linijki: powłoka i moduły to dwa różne builds (Vite vs Next)
 * z osobnymi konfiguracjami Tailwinda, więc wspólny komponent i tak nie
 * przeszedłby granicy. Wspólny jest KONTRAKT DANYCH, nie CSS.
 *
 * Trzy zasady, które ten kafel trzyma:
 *
 *  1. `—` zamiast `0`, gdy nie ma danych. Zero nadgodzin to wynik; brak sesji
 *     pracy to brak wyniku. Rysowanie obu tak samo to kłamstwo, na którym
 *     kadrowa podejmuje decyzję.
 *  2. Wielkość próbki jest widoczna. „92% (3 pomiary)" i „92% (300 pomiarów)"
 *     to nie jest ta sama informacja, a wyglądały identycznie.
 *  3. Żadnej zbiorczej oceny. Kafel pokazuje liczby SWOJEGO organu i nigdy nie
 *     miesza ich z cudzymi.
 */

interface ModuleScorecardProps {
  card: WorkerModuleScorecard;
  workerName: string;
  canManage: boolean;
  onAssign: (moduleSlug: string) => void;
}

export function ModuleScorecard({ card, workerName, canManage, onAssign }: ModuleScorecardProps) {
  const navigate = useNavigate();
  const def = getModuleDef(card.module);
  if (!def) return null;

  const Icon = def.icon;
  const state = workerModuleState(card.has_access, card.is_measured);
  const measuredMetrics = card.metrics.filter((m) => m.sample > 0);

  return (
    <div
      className={`rounded-ids-lg border bg-gray-800/60 overflow-hidden ${
        state === 'orphan' ? 'border-amber-500/40' : def.color.border
      }`}
    >
      {/* Nagłówek — ikona i barwa organu */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-700/60">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`p-1.5 rounded-ids-md ${def.color.bg} ${def.color.text}`}>
            <Icon className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-aurora-text truncate">{def.navName}</p>
            <p className="text-[11px] text-gray-500 truncate">{def.displayName}</p>
          </div>
        </div>
        {card.deep_link && (
          <button
            type="button"
            onClick={() => navigate(card.deep_link as string)}
            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-aurora-text transition-colors flex-shrink-0"
            title={`Otwórz ${workerName} w module ${def.navName}`}
          >
            Otwórz w module
            <ArrowUpRight className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Stan bez danych — pełnoprawny komunikat, nie pusty kafel */}
      {!card.is_measured ? (
        <div className="px-4 py-5 text-center">
          {state === 'unassigned' ? (
            <>
              <p className="text-sm text-gray-400">Nieprzypisany do tego organu</p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => onAssign(card.module)}
                  className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-ids-md text-xs font-medium transition-colors ${def.color.bg} ${def.color.text} hover:brightness-125`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  Przypisz do {def.navName}
                </button>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400">Przypisany, brak danych z ostatnich dni</p>
              <p className="text-[11px] text-gray-600 mt-1">
                Okno: {card.window_days} dni. Zmień zakres u góry, żeby sięgnąć dalej.
              </p>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Metryka wiodąca — to, czym organ się przedstawia */}
          {card.headline && (
            <div className="px-4 pt-4 pb-2">
              <p className="text-[11px] uppercase tracking-wider text-gray-500">
                {card.headline.label}
              </p>
              <div className="flex items-baseline gap-2">
                <span
                  className={`text-3xl font-bold tabular-nums ${TONE_TEXT[card.headline.tone]}`}
                >
                  {formatWorkerMetric(card.headline)}
                </span>
                {metricSampleNote(card.headline) && (
                  <span className="text-[11px] text-amber-400/80">
                    {metricSampleNote(card.headline)}
                  </span>
                )}
              </div>
              {card.headline.hint && (
                <p className="text-[11px] text-gray-600 mt-1 leading-snug">{card.headline.hint}</p>
              )}
            </div>
          )}

          {/* Pozostałe metryki organu */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-4 pb-3">
            {measuredMetrics
              .filter((m) => m.key !== card.headline?.key)
              .map((metric) => (
                <div key={metric.key} title={metric.hint}>
                  <p className="text-[11px] text-gray-500 truncate">{metric.label}</p>
                  <p className={`text-sm font-semibold tabular-nums ${TONE_TEXT[metric.tone]}`}>
                    {formatWorkerMetric(metric)}
                  </p>
                </div>
              ))}
          </div>

          <div className="flex items-center justify-between px-4 py-2 border-t border-gray-700/60 text-[11px]">
            <span className="text-gray-500">
              Ostatnia aktywność: {formatRelativeDate(card.last_activity_at)}
            </span>
            {state === 'orphan' && (
              <span className="flex items-center gap-1 text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                bez przypisania
              </span>
            )}
          </div>

          {/* Sygnał niespójności — mierzony, ale niewidoczny na liście organu */}
          {state === 'orphan' && canManage && (
            <div className="px-4 pb-3">
              <p className="text-[11px] text-amber-300/80 mb-2">
                Ten organ ma dane pracownika, ale nie jest on do niego przypisany — dlatego nie
                widać go na liście {def.navName}.
              </p>
              <button
                type="button"
                onClick={() => onAssign(card.module)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-ids-md text-xs font-medium bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Przypisz do {def.navName}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
